import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { EmailLinksEditor } from 'components/EmailLinksEditor'
import type { InputType } from 'config'
import { EmailAssigneeOptions } from 'pages/Tasks/constants'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { getOriginalTemplate, submitEmailTemplate } from 'services/apis/conditions'
import { resetTeamEmailTemplate, upsertTeamEmailTemplate } from 'services/apis/team'
import { resetUserEmailTemplate, upsertUserEmailTemplate } from 'services/apis/user'
import { svgLoading } from 'stories/assets'
import { Button, FormTable, Modal } from 'stories/components'
import {
  changeObject2Array,
  confirm,
  getPartiesMap,
  getUnknownKeyInPartiesEmailTemplate,
  InputConvert,
  InputValidate,
} from 'utils'
import { getEmailHtml } from 'utils/emailHtml'
import { RenderInput } from 'utils/RenderInput'

import {
  availabilityPayload,
  availabilityValues,
  useTemplateAvailabilityOptions,
} from '../useTemplateAvailability'
import { automationInputs, defaultInputs } from './constants'
import { type EmailTemplate, type IEmailButton, ILoanEmailAutomation } from './type'

interface EmailTemplateDetailsProps {
  emailFrom: Record<string, string>
  purposeOptions: Record<string, Record<string, string>>
  elementOptions: Record<string, Record<string, string>>
  defaults: EmailTemplate | null
  onBack: Function
  onComplete: Function
  /** When set, edits save as a team override (no create/delete, locked group/purpose). */
  teamId?: number
  /** When set, edits save as a user override (no create/delete, locked group/purpose). */
  userId?: number
  isCustomized?: boolean
  /**
   * Who a NEW template starts out available to. The picker passes the person adding it, so a
   * template created mid-conversation is theirs rather than another company-wide entry in
   * everyone's list. They can widen it in the same form before saving.
   */
  defaultAvailableUserIds?: number[]
}

export function EmailTemplateDetails(props: EmailTemplateDetailsProps) {
  const [isLoading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [links, setLinks] = useState<IEmailButton[]>([])
  const [automations, setAutomations] = useState<ILoanEmailAutomation[]>([])
  const [showError, setShowError] = useState(false)
  const [loanGlobalStatus] = useSelector((state: any) => [state.loanGlobalStatus])
  const [originalTemplate, setOriginalTemplate] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [previewHtml, setPreviewHtml] = useState('')
  const [cursorPos, setCursorPos] = useState(0)
  const [focusField, setFocusField] = useState<string>('')
  const { defaults = null, teamId, userId, isCustomized, defaultAvailableUserIds } = props
  const isTeamMode = !!teamId
  const isUserMode = !!userId
  const isOverrideMode = isTeamMode || isUserMode
  const { profileOptions, userOptions, teamOptions } = useTemplateAvailabilityOptions(!isOverrideMode)

  const activeInputRef = useRef(null)

  useEffect(() => {
    if (isOverrideMode) return
    setInputs((prev) => {
      if (!prev.availableUserIds && !prev.availableTeamIds) return prev
      const next = cloneDeep(prev)
      if (next.availableUserIds) (next.availableUserIds as any).options = userOptions
      if (next.availableTeamIds) (next.availableTeamIds as any).options = teamOptions
      return next
    })
  }, [userOptions, teamOptions, isOverrideMode])

  useEffect(() => {
    onUpdateDefaults()
    getOriginalTemplate().then((res) => {
      setOriginalTemplate(res)
    })
  }, [defaults, isOverrideMode])

  const onUpdateDefaults = async () => {
    let inputs = cloneDeep(defaultInputs())
    let purposeOptions = {}
    Object.keys(props.purposeOptions).forEach((key) => {
      const newOptions: Record<string, string> = {}
      Object.keys(props.purposeOptions[key]).forEach((itemKey) => {
        newOptions[itemKey] = `${props.purposeOptions[key][itemKey]}`
      })
      purposeOptions = {
        ...purposeOptions,
        ...newOptions,
      }
    })
    ;(inputs.purpose as any).options = purposeOptions
    ;(inputs.from as any).options = props.emailFrom
    ;(inputs.content as any).placeholders = Object.keys(getPartiesMap({})).sort()
    // Titles and tooltips come with availabilityInputs(); only the options are dynamic.
    ;(inputs.availableAccountTypes as any).options = profileOptions()
    ;(inputs.availableUserIds as any).options = userOptions
    ;(inputs.availableTeamIds as any).options = teamOptions

    if (isOverrideMode) {
      delete inputs.notePageTypes
      delete inputs.availableAccountTypes
      delete inputs.availableUserIds
      delete inputs.availableTeamIds
    }

    if (!defaults) {
      if (!isOverrideMode && defaultAvailableUserIds?.length && inputs.availableUserIds) {
        inputs.availableUserIds.value = defaultAvailableUserIds.map((id) => String(id))
      }
      setInputs(inputs)
      return
    }
    const availability: Record<string, any> = availabilityValues(defaults)
    for (const key in inputs) {
      if (availability[key] !== undefined) {
        inputs[key].value = availability[key]
      } else if (key === 'notePageTypes') {
        inputs[key].value = defaults.notePageTypes || []
      } else {
        inputs[key].value = (defaults as any)[key]
      }
    }
    inputs = updateElementOptions(inputs, defaults.group)
    if (isOverrideMode) {
      inputs.group.disabled = true
      inputs.purpose.disabled = true
    }
    setInputs(inputs)
    setLinks(defaults?.links || [])
    setAutomations(defaults?.automations || [])
  }

  const onBack = () => {
    props.onBack()
  }

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''
    if (key == 'group') newInputs = updateElementOptions(newInputs, value, true)
    setInputs(newInputs)
  }

  const handleFocus = (event: any, fieldKey: string) => {
    activeInputRef.current = event.target
    setCursorPos(event.target.selectionStart)
    setFocusField(fieldKey)
  }

  const handleCursorChange = (event: any) => {
    setCursorPos(event.target.selectionStart)
  }

  const insertTextAtCursor = (text: string) => {
    const input: any = activeInputRef.current
    if (!input) return

    const value = input.value
    const newValue = value.substring(0, cursorPos) + text + value.substring(cursorPos)
    const newCursorPos = cursorPos + text.length

    onChange(focusField, newValue)
    setTimeout(() => {
      input.focus({ preventScroll: true })
      input.setSelectionRange(newCursorPos, newCursorPos)
      setCursorPos(newCursorPos)
    }, 0)
  }

  const updateElementOptions = (inputs: Record<string, InputType>, emailGroup: string, setSelectAll = false) => {
    ;(inputs.elements as any).options = props.elementOptions[emailGroup] || {}
    if (setSelectAll) (inputs.elements as any).value = Object.keys(props.elementOptions[emailGroup] || {})
    return inputs
  }

  const onSubmit = () => {
    let hasError = false
    const newStats = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in newStats) {
      const { value } = newStats[key]
      let error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) hasError = true
      if (value !== undefined) data[key] = value
    }

    links.map((item) => {
      if (!item.label || !item.url) hasError = true
    })
    data.links = links
    if (!isOverrideMode) {
      data.automations = automations
      data.notePageTypes = changeObject2Array(data.notePageTypes)
      Object.assign(data, availabilityPayload(data, { userOptions, teamOptions }))
    } else {
      delete data.notePageTypes
      delete data.availableAccountTypes
      delete data.availableUserIds
      delete data.availableTeamIds
    }

    if (hasError) {
      setInputs(newStats)
      setShowError(true)
      return
    }
    const { subject, content = '' } = data
    let unknownKey = getUnknownKeyInPartiesEmailTemplate(content)
    if (unknownKey) {
      return toast(`${unknownKey} is unknown variable in "Content".`, { type: 'warning' })
    }

    unknownKey = getUnknownKeyInPartiesEmailTemplate(subject)
    if (unknownKey) {
      return toast(`${unknownKey} is unknown variable in "Type".`, { type: 'warning' })
    }

    setLoading(true)
    const id = defaults ? defaults.id : 0

    const overridePayload = {
      from: data.from,
      to: data.to,
      cc: data.cc,
      subject: data.subject,
      title: data.title,
      elements: data.elements,
      links: data.links,
      content: data.content,
    }

    const savePromise =
      isTeamMode && teamId
        ? upsertTeamEmailTemplate(teamId, id, overridePayload)
        : isUserMode && userId
          ? upsertUserEmailTemplate(userId, id, overridePayload)
          : submitEmailTemplate(id, { ...data })

    savePromise
      .then(() => {
        if (isTeamMode) toast(`Team email template "${defaults?.subject}" is updated.`, { type: 'success' })
        else if (isUserMode) toast(`User email template "${defaults?.subject}" is updated.`, { type: 'success' })
        else if (id == 0) toast('New Email Template is added.', { type: 'success' })
        else toast(`Email Template "${defaults?.subject}" is updated.`, { type: 'success' })
        props.onComplete()
        onBack()
      })
      .catch(() => setLoading(false))
  }

  const onResetOverride = async () => {
    if (!isOverrideMode || !defaults?.id || !isCustomized) return
    const label = isUserMode ? 'user' : 'team'
    const message = (
      <div className="text-gray-500 mb-2">
        Reset this template to the default version? Your {label} customizations will be removed.
      </div>
    )
    const ok = await confirm(message)
    if (!ok) return
    setLoading(true)
    const resetPromise =
      isTeamMode && teamId
        ? resetTeamEmailTemplate(teamId, defaults.id)
        : resetUserEmailTemplate(userId!, defaults.id)

    resetPromise
      .then(() => {
        toast('Template reset to default.', { type: 'success' })
        props.onComplete()
        onBack()
      })
      .catch(() => setLoading(false))
  }

  const placeholders = getPartiesMap({})

  const renderAutomations = () => {
    const onSubmit = (currentId: any, values: Record<string, any>) => {
      const temp = cloneDeep(automations)
      if (!currentId) {
        if (automations.length === 0) values.id = 1
        else values.id = automations[automations.length - 1].id + 1
        temp.push(values as any)
      } else {
        temp.forEach((item: any, index: number) => {
          if (item.id == currentId) {
            values.id = temp[index].id
            temp[index] = values as any
          }
        })
      }
      setAutomations(temp)
      return values
    }

    const onRemove = (id: any) => {
      return new Promise((resolve) => {
        if (id) {
          const temp = automations.filter((item) => item.id != id)
          setAutomations(temp)
        }
        resolve(true)
      })
    }

    return (
      <div className="col-span-full">
        <p className="text-section mb-4">- Automations</p>
        <FormTable
          header={[
            {
              key: 'loanStatus',
              title: 'Loan Status',
              value: (row: ILoanEmailAutomation) => {
                return (
                  <div>
                    {Object.keys(row.loanStatus)
                      .filter((v) => row.loanStatus[v])
                      .map((v) => loanGlobalStatus[v])
                      .join(', ')}
                  </div>
                )
              },
            },
            { key: 'duration', title: 'Duration' },
            {
              key: 'to',
              title: 'Email To',
              value: (row: ILoanEmailAutomation) => {
                return (
                  <div>
                    {Object.keys(row.to || {})
                      .filter((v) => row.to[v])
                      .map((v) => EmailAssigneeOptions()[v])
                      .join(', ')}
                  </div>
                )
              },
            },
          ]}
          addText="Add Automation"
          permission={1}
          inputs={automationInputs(loanGlobalStatus)}
          data={automations}
          onSubmit={onSubmit}
          onRemove={onRemove}
        />
      </div>
    )
  }

  const onPreview = () => {
    const elementList: any[] = []
    if (!inputs.group.value) return
    const emailOptions = props.elementOptions[inputs.group.value]
    Object.keys(emailOptions).forEach((elementKey) => {
      if (inputs.elements?.value.includes(elementKey))
        elementList.push({
          type: 'element3',
          data: [emailOptions[elementKey], `{{${emailOptions[elementKey]}}}`],
        })
    })

    const message = getEmailHtml(
      [
        ...elementList,
        {
          type: 'element2',
          data: [
            `<div style='font-family:sans-serif; text-align: left'>${(inputs.content.value || '')
              .split('\n')
              .join('<br/>')}</div>`,
          ],
        },
        ...(links || []).map((link) => {
          return {
            type: link.type === 'button' ? 'element6' : 'link',
            data: [link.url, link.label],
          }
        }),
      ],
      originalTemplate,
    )
    setShowModal(true)
    setPreviewHtml(message)
  }

  return (
    <div className="email-template-container">
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Email Template -{' '}
        {isUserMode ? 'User Override' : isTeamMode ? 'Team Override' : !defaults ? 'Add' : `Update`}
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <Button link onClick={onBack}>
        <div className="flex text-shade-blue items-center">
          <ArrowLeftIcon className="w-4 h-4 mr-1" /> <p>Return to Email Template</p>
        </div>
      </Button>
      {isOverrideMode && (
        <p className="text-sm text-gray-500 mt-2 mb-1">
          Group and purpose stay company-wide. Saving creates a {isUserMode ? 'user' : 'team'}-specific copy of the
          content fields.
          {isCustomized ? ` This ${isUserMode ? 'user' : 'team'} already has a customized version.` : ''}
        </p>
      )}

      <div className="my-4">
        <div className="border-b mb-2">- Placeholders</div>
        <pre className="flex gap-1 flex-wrap">
          {Object.keys(placeholders)
            .sort()
            .map((key, index) => {
              return (
                <div
                  key={index}
                  className="mr-2 cursor-pointer hover:underline"
                  onClick={() => insertTextAtCursor(`{{${key}}}`)}
                >
                  {key},{' '}
                </div>
              )
            })}
        </pre>
      </div>
      <div className="my-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.keys(inputs).map((key, index) => {
          const input = inputs[key]
          return (
            <div className={`input ${input.span ? 'col-span-full' : 'col-span-1'}`} key={index}>
              <RenderInput
                input={input}
                Key={key}
                onChange={onChange}
                onFocus={(e: any) => handleFocus(e, key)}
                onKeyUp={handleCursorChange}
                onClick={handleCursorChange}
              />
            </div>
          )
        })}
        <EmailLinksEditor value={links} onChange={setLinks} properties={{ showError }} />

        {!isOverrideMode && renderAutomations()}
      </div>

      <div className="block">
        <Button onClick={onSubmit} className="px-10" loading={isLoading}>
          <>
            {isUserMode ? 'Save User Template' : isTeamMode ? 'Save Team Template' : defaults ? 'Update' : 'Add'}
          </>
        </Button>
        <Button onClick={onPreview} color="gray">
          Preview
        </Button>
        {isOverrideMode && isCustomized && (
          <Button onClick={onResetOverride} color="red" loading={isLoading}>
            Reset to Default
          </Button>
        )}
      </div>
      {showModal && (
        <Modal title={'Email Preview'} isOpen onClose={() => setShowModal(false)} titleOkay="" titleCancel="">
          <div dangerouslySetInnerHTML={{ __html: previewHtml }}></div>
        </Modal>
      )}
    </div>
  )
}
